import { useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Hook para generar columnas skeleton durante la carga
 *
 * @param columns - Las columnas originales de la tabla
 * @param isLoading - Estado de carga
 * @returns Columnas con skeleton cells o columnas originales
 *
 * @example
 * ```tsx
 * const skeletonColumns = useSkeletonColumns(columns, isLoading)
 *
 * const table = useReactTable({
 *   data: isLoading ? skeletonData : realData,
 *   columns: skeletonColumns,
 *   ...
 * })
 * ```
 */
export function useSkeletonColumns<TData, TValue>(
  columns: ColumnDef<TData, TValue>[],
  isLoading: boolean
): ColumnDef<TData, TValue>[] {
  return useMemo(() => {
    if (!isLoading) return columns

    return columns.map((column) => {
      const width = '80%'
      const height = '20px'

      return {
        ...column,
        cell: () => (
          <Skeleton
            className={`h-[${height}] w-[${width}] rounded`}
            style={{ width, height }}
          />
        ),
      }
    })
  }, [columns, isLoading])
}

/**
 * Genera datos skeleton para mostrar durante la carga
 *
 * @param rowCount - Cantidad de filas skeleton a generar
 * @returns Array de objetos con id skeleton
 *
 * @example
 * ```tsx
 * const skeletonData = generateSkeletonData(10)
 *
 * const table = useReactTable({
 *   data: isLoading ? skeletonData : realData,
 *   ...
 * })
 * ```
 */
export function generateSkeletonData<T = { id: string }>(
  rowCount: number
): T[] {
  return Array.from({ length: rowCount }, (_, index) => ({
    id: `skeleton-${index}`,
  })) as T[]
}

/**
 * Verifica si una fila es un skeleton
 *
 * @param row - La fila a verificar
 * @returns true si es skeleton, false en caso contrario
 *
 * @example
 * ```tsx
 * const handleRowClick = (row) => {
 *   if (isSkeletonRow(row)) return
 *   // Procesar row real
 * }
 * ```
 */
export function isSkeletonRow(row: unknown): boolean {
  return (
    typeof row === 'object' &&
    row !== null &&
    'id' in row &&
    typeof (row as { id?: string }).id === 'string' &&
    (row as { id: string }).id.startsWith('skeleton-')
  )
}

/**
 * Componente de Skeleton para celdas de tabla
 * Útil para casos personalizados
 */
export function TableCellSkeleton({
  width = '80%',
  height = '20px',
}: {
  width?: string
  height?: string
}) {
  return (
    <Skeleton
      className={`h-[${height}] w-[${width}] rounded`}
      style={{ width, height }}
    />
  )
}
